'use client';

import { c as _c } from "react/compiler-runtime";
import './gift-catalog-core.scss';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ElementType } from 'react';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Prominence, Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { ChipGroup } from '@sola/ark-core/molecules/chip-group/chip-group.tsx';
import { Form } from '@sola/ark-core/molecules/form/form.tsx';
import type { SelectItemProps } from '@sola/ark-core/molecules/select/select.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { SliderInput } from '@sola/ark-core/molecules/slider-input/slider-input.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { generalConfig } from '@sola/config/lib/getters.ts';
import type { BlockProps, FundProps } from '@sola/schema/lib/schema.ts';
import type { SearchResultProps } from '@sola/skippy-search-sdk/lib/schema.ts';
import { TrackViewFundItemList, trackClick, trackSelectFundItem } from '@sola/tracking/components/datalayer.tsx';
import { GiftCatalogCard } from '../../../molecules/gift-catalog-card/gift-catalog-card.tsx';
import { HeaderGroup } from '../../../molecules/header-group/header-group.tsx';
import { GiftCatalogSortEnum } from '../../../utils/query-params.tsx';
import { GiftCatalogCoreCardsSkeleton, GiftCatalogCoreSkeleton } from './gift-catalog-core-skeleton.tsx';
import { useGiftCatalogCore } from './gift-catalog-core.logic.ts';
const queryClient = new QueryClient();
const InternalGiftCatalogCore: React.FC<BlockProps> = t0 => {
  const $ = _c(210);
  const {
    seoLevel: t1
  } = t0;
  const seoLevel = t1 === undefined ? 2 : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["gift-catalog", "skippy"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation(t2);
  const {
    isCategoryOpen,
    setIsCategoryOpen,
    isPriceOpen,
    setIsPriceOpen,
    isMobileFiltersOpen,
    setIsMobileFiltersOpen,
    currentPage,
    selectedCategoryFilters,
    categories,
    priceRange,
    isPriceFiltered,
    giftCatalogFilters,
    allResults,
    searchResults,
    isLoading,
    error,
    hasMore,
    handlePriceChange,
    handleActiveFilterChange,
    handleSortChange,
    handleLoadMore,
    resetAllFilters,
    resetPriceFilter
  } = useGiftCatalogCore();
  let Comp2;
  let t10;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== categories || $[2] !== error || $[3] !== giftCatalogFilters || $[4] !== handleActiveFilterChange || $[5] !== handlePriceChange || $[6] !== handleSortChange || $[7] !== isCategoryOpen || $[8] !== isMobileFiltersOpen || $[9] !== isPriceFiltered || $[10] !== isPriceOpen || $[11] !== priceRange || $[12] !== resetAllFilters || $[13] !== resetPriceFilter || $[14] !== selectedCategoryFilters || $[15] !== seoLevel || $[16] !== setIsCategoryOpen || $[17] !== setIsMobileFiltersOpen || $[18] !== setIsPriceOpen || $[19] !== t) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const categoryLabels = t("gift-catalog:taxonomy.fundCategories", {
        returnObjects: true
      }) as Record<string, string>;
      let t11;
      if ($[29] !== t) {
        t11 = t("gift-catalog:sort.mostNeeded");
        $[29] = t;
        $[30] = t11;
      } else {
        t11 = $[30];
      }
      let t12;
      if ($[31] !== t11) {
        t12 = {
          value: GiftCatalogSortEnum.enum.mostNeeded,
          text: t11
        };
        $[31] = t11;
        $[32] = t12;
      } else {
        t12 = $[32];
      }
      let t13;
      if ($[33] !== t) {
        t13 = t("gift-catalog:sort.priceLowToHigh");
        $[33] = t;
        $[34] = t13;
      } else {
        t13 = $[34];
      }
      let t14;
      if ($[35] !== t13) {
        t14 = {
          value: GiftCatalogSortEnum.enum.priceLowToHigh,
          text: t13
        };
        $[35] = t13;
        $[36] = t14;
      } else {
        t14 = $[36];
      }
      let t15;
      if ($[37] !== t) {
        t15 = t("gift-catalog:sort.priceHighToLow");
        $[37] = t;
        $[38] = t15;
      } else {
        t15 = $[38];
      }
      let t16;
      if ($[39] !== t15) {
        t16 = {
          value: GiftCatalogSortEnum.enum.priceHighToLow,
          text: t15
        };
        $[39] = t15;
        $[40] = t16;
      } else {
        t16 = $[40];
      }
      let t17;
      if ($[41] !== t) {
        t17 = t("gift-catalog:sort.alphabeticalAZ");
        $[41] = t;
        $[42] = t17;
      } else {
        t17 = $[42];
      }
      let t18;
      if ($[43] !== t17) {
        t18 = {
          value: GiftCatalogSortEnum.enum.alphabeticalAZ,
          text: t17
        };
        $[43] = t17;
        $[44] = t18;
      } else {
        t18 = $[44];
      }
      let t19;
      if ($[45] !== t) {
        t19 = t("gift-catalog:sort.alphabeticalZA");
        $[45] = t;
        $[46] = t19;
      } else {
        t19 = $[46];
      }
      let t20;
      if ($[47] !== t19) {
        t20 = {
          value: GiftCatalogSortEnum.enum.alphabeticalZA,
          text: t19
        };
        $[47] = t19;
        $[48] = t20;
      } else {
        t20 = $[48];
      }
      let t21;
      if ($[49] !== t12 || $[50] !== t14 || $[51] !== t16 || $[52] !== t18 || $[53] !== t20) {
        t21 = [t12, t14, t16, t18, t20];
        $[49] = t12;
        $[50] = t14;
        $[51] = t16;
        $[52] = t18;
        $[53] = t20;
        $[54] = t21;
      } else {
        t21 = $[54];
      }
      const sortOptions = t21;
      Comp2 = `h${seoLevel + 1}` as ElementType;
      const Comp3 = `h${seoLevel + 2}` as ElementType;
      if (error) {
        let t22;
        if ($[55] !== t) {
          t22 = t("gift-catalog:error");
          $[55] = t;
          $[56] = t22;
        } else {
          t22 = $[56];
        }
        let t23;
        if ($[57] !== t22) {
          t23 = <section className="GiftCatalogCore component"><div className="container"><Alert variant="negative" message={t22} /></div></section>;
          $[57] = t22;
          $[58] = t23;
        } else {
          t23 = $[58];
        }
        t10 = t23;
        break bb0;
      }
      t9 = "GiftCatalogCore component";
      t7 = "GiftCatalogCore__container container grid gap-comfy-sm md:gap-comfy-xl";
      let t22;
      if ($[59] !== t) {
        t22 = t("gift-catalog:viewAllGifts");
        $[59] = t;
        $[60] = t22;
      } else {
        t22 = $[60];
      }
      let t23;
      if ($[61] !== t) {
        t23 = t("gift-catalog:subHeading");
        $[61] = t;
        $[62] = t23;
      } else {
        t23 = $[62];
      }
      let t24;
      if ($[63] !== t23) {
        t24 = <p>{t23}</p>;
        $[63] = t23;
        $[64] = t24;
      } else {
        t24 = $[64];
      }
      let t25;
      if ($[65] !== seoLevel || $[66] !== t22 || $[67] !== t24) {
        t25 = <div className="flex-1 "><HeaderGroup seoLevel={seoLevel} title={t22} body={t24} /></div>;
        $[65] = seoLevel;
        $[66] = t22;
        $[67] = t24;
        $[68] = t25;
      } else {
        t25 = $[68];
      }
      let t26;
      if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
        t26 = <ArkIcon iconName="settings-04" />;
        $[69] = t26;
      } else {
        t26 = $[69];
      }
      let t27;
      if ($[70] !== isMobileFiltersOpen || $[71] !== setIsMobileFiltersOpen) {
        t27 = () => setIsMobileFiltersOpen(!isMobileFiltersOpen);
        $[70] = isMobileFiltersOpen;
        $[71] = setIsMobileFiltersOpen;
        $[72] = t27;
      } else {
        t27 = $[72];
      }
      let t28;
      if ($[73] !== selectedCategoryFilters.length || $[74] !== t) {
        t28 = selectedCategoryFilters.length > 0 ? t("gift-catalog:filterBy", {
          count: selectedCategoryFilters.length
        }) : t("gift-catalog:filterByWithoutCount");
        $[73] = selectedCategoryFilters.length;
        $[74] = t;
        $[75] = t28;
      } else {
        t28 = $[75];
      }
      let t29;
      if ($[76] !== t27 || $[77] !== t28) {
        t29 = <div className="GiftCatalogCore__filterToggle md:hidden flex-1"><ButtonStyle variant="primary" size="md" iconAfter={t26}><button type="button" className="justify-between w-full" onClick={t27}>{t28}</button></ButtonStyle></div>;
        $[76] = t27;
        $[77] = t28;
        $[78] = t29;
      } else {
        t29 = $[78];
      }
      let t30;
      if ($[79] !== t) {
        t30 = t("gift-catalog:sortBy");
        $[79] = t;
        $[80] = t30;
      } else {
        t30 = $[80];
      }
      let t31;
      if ($[81] !== Comp2 || $[82] !== t30) {
        t31 = <div className="hidden md:block w-full"><Text variant={Variants.Label} size={Sizes.Medium} asChild={true}><Comp2>{t30}</Comp2></Text></div>;
        $[81] = Comp2;
        $[82] = t30;
        $[83] = t31;
      } else {
        t31 = $[83];
      }
      const t32 = giftCatalogFilters.sortby;
      let t33;
      if ($[84] !== handleSortChange) {
        t33 = e => handleSortChange(e.target.value);
        $[84] = handleSortChange;
        $[85] = t33;
      } else {
        t33 = $[85];
      }
      let t34;
      if ($[86] !== t) {
        t34 = t("gift-catalog:sortGiftsBy");
        $[86] = t;
        $[87] = t34;
      } else {
        t34 = $[87];
      }
      let t35;
      if ($[88] !== giftCatalogFilters.sortby || $[89] !== sortOptions || $[90] !== t33 || $[91] !== t34) {
        t35 = <Select name="sort" items={sortOptions} defaultValue={t32} onChange={t33} aria-label={t34} />;
        $[88] = giftCatalogFilters.sortby;
        $[89] = sortOptions;
        $[90] = t33;
        $[91] = t34;
        $[92] = t35;
      } else {
        t35 = $[92];
      }
      let t36;
      if ($[93] !== t31 || $[94] !== t35) {
        t36 = <div className="GiftCatalogCore__sort grid gap-snug-lg relative flex-1">{t31}{t35}</div>;
        $[93] = t31;
        $[94] = t35;
        $[95] = t36;
      } else {
        t36 = $[95];
      }
      let t37;
      if ($[96] !== t29 || $[97] !== t36) {
        t37 = <div className="flex gap-comfy-sm">{t29}{t36}</div>;
        $[96] = t29;
        $[97] = t36;
        $[98] = t37;
      } else {
        t37 = $[98];
      }
      if ($[99] !== t25 || $[100] !== t37) {
        t8 = <div className="GiftCatalogCore__header flex flex-col gap-comfy-md md:items-end md:flex-row ">{t25}{t37}</div>;
        $[99] = t25;
        $[100] = t37;
        $[101] = t8;
      } else {
        t8 = $[101];
      }
      t5 = "GiftCatalogCore__body grid items-start gap-comfy-lg";
      const t38 = !isMobileFiltersOpen;
      let t39;
      if ($[102] !== isMobileFiltersOpen || $[103] !== t38) {
        t39 = cn("GiftCatalogCore__filters grid px-comfy-sm py-comfy-lg rounded-lg", "md:grid", {
          hidden: t38,
          grid: isMobileFiltersOpen
        });
        $[102] = isMobileFiltersOpen;
        $[103] = t38;
        $[104] = t39;
      } else {
        t39 = $[104];
      }
      let t40;
      if ($[105] !== selectedCategoryFilters.length || $[106] !== t) {
        t40 = selectedCategoryFilters.length > 0 ? t("gift-catalog:filterBy", {
          count: selectedCategoryFilters.length
        }) : t("gift-catalog:filterByWithoutCount");
        $[105] = selectedCategoryFilters.length;
        $[106] = t;
        $[107] = t40;
      } else {
        t40 = $[107];
      }
      let t41;
      if ($[108] !== Comp2 || $[109] !== t40) {
        t41 = <Text variant={Variants.Heading} size={Sizes.XSmall} asChild={true}><Comp2>{t40}</Comp2></Text>;
        $[108] = Comp2;
        $[109] = t40;
        $[110] = t41;
      } else {
        t41 = $[110];
      }
      let t42;
      if ($[111] !== resetAllFilters || $[112] !== t) {
        t42 = () => {
          trackClick(t("gift-catalog:clearAll"));
          resetAllFilters();
        };
        $[111] = resetAllFilters;
        $[112] = t;
        $[113] = t42;
      } else {
        t42 = $[113];
      }
      let t43;
      if ($[114] !== t) {
        t43 = t("gift-catalog:clearAll");
        $[114] = t;
        $[115] = t43;
      } else {
        t43 = $[115];
      }
      let t44;
      if ($[116] !== t42 || $[117] !== t43) {
        t44 = <LinkStyle size="lg" weightOverride="normal" noPadding={true}><button type="button" onClick={t42}>{t43}</button></LinkStyle>;
        $[116] = t42;
        $[117] = t43;
        $[118] = t44;
      } else {
        t44 = $[118];
      }
      let t45;
      if ($[119] !== t41 || $[120] !== t44) {
        t45 = <div className="GiftCatalogCore__filtersHeader flex items-center justify-between">{t41}{t44}</div>;
        $[119] = t41;
        $[120] = t44;
        $[121] = t45;
      } else {
        t45 = $[121];
      }
      let t46;
      if ($[122] !== isCategoryOpen) {
        t46 = isCategoryOpen ? <ArkIcon iconName="chevron-up" /> : <ArkIcon iconName="chevron-down" />;
        $[122] = isCategoryOpen;
        $[123] = t46;
      } else {
        t46 = $[123];
      }
      let t47;
      if ($[124] !== isCategoryOpen || $[125] !== setIsCategoryOpen) {
        t47 = () => setIsCategoryOpen(!isCategoryOpen);
        $[124] = isCategoryOpen;
        $[125] = setIsCategoryOpen;
        $[126] = t47;
      } else {
        t47 = $[126];
      }
      const t48 = `GiftCatalogCore__filterButton justify-between -mx-comfy-xs mt-comfy-xs mb-comfy-xs ${isCategoryOpen ? "" : "is-collapsed"}`;
      let t49;
      if ($[127] !== t) {
        t49 = t("gift-catalog:category");
        $[127] = t;
        $[128] = t49;
      } else {
        t49 = $[128];
      }
      let t50;
      if ($[129] !== Comp3 || $[130] !== t49) {
        t50 = <Text variant={Variants.Label} size={Sizes.Large} prominence={Prominence.Potent} asChild={true}><Comp3>{t49}</Comp3></Text>;
        $[129] = Comp3;
        $[130] = t49;
        $[131] = t50;
      } else {
        t50 = $[131];
      }
      let t51;
      if ($[132] !== t47 || $[133] !== t48 || $[134] !== t50) {
        t51 = <button type="button" onClick={t47} className={t48}>{t50}</button>;
        $[132] = t47;
        $[133] = t48;
        $[134] = t50;
        $[135] = t51;
      } else {
        t51 = $[135];
      }
      let t52;
      if ($[136] !== t46 || $[137] !== t51) {
        t52 = <ButtonStyle variant="secondary" iconAfter={t46}>{t51}</ButtonStyle>;
        $[136] = t46;
        $[137] = t51;
        $[138] = t52;
      } else {
        t52 = $[138];
      }
      let t53;
      if ($[139] !== categories || $[140] !== handleActiveFilterChange || $[141] !== isCategoryOpen || $[142] !== selectedCategoryFilters || $[143] !== t) {
        t53 = isCategoryOpen && <div className="GiftCatalogCore__filterBody pb-comfy-sm"><ChipGroup chips={categories.map(c => ({
            ...c,
            name: t(`gift-catalog:taxonomy.fundCategories.${c.value}`)
          })).sort(_temp)} handleSelectionChange={handleActiveFilterChange} className="gap-snug-xl" selectedValues={selectedCategoryFilters} chipSize="md" /></div>;
        $[139] = categories;
        $[140] = handleActiveFilterChange;
        $[141] = isCategoryOpen;
        $[142] = selectedCategoryFilters;
        $[143] = t;
        $[144] = t53;
      } else {
        t53 = $[144];
      }
      let t54;
      if ($[145] !== t52 || $[146] !== t53) {
        t54 = <div className="GiftCatalogCore__filterSection grid">{t52}{t53}</div>;
        $[145] = t52;
        $[146] = t53;
        $[147] = t54;
      } else {
        t54 = $[147];
      }
      let t55;
      if ($[148] === Symbol.for("react.memo_cache_sentinel")) {
        t55 = <hr className="GiftCatalogCore__divider block m-none" />;
        $[148] = t55;
      } else {
        t55 = $[148];
      }
      let t56;
      if ($[149] !== isPriceOpen) {
        t56 = isPriceOpen ? <ArkIcon iconName="chevron-up" /> : <ArkIcon iconName="chevron-down" />;
        $[149] = isPriceOpen;
        $[150] = t56;
      } else {
        t56 = $[150];
      }
      let t57;
      if ($[151] !== isPriceOpen || $[152] !== setIsPriceOpen) {
        t57 = () => setIsPriceOpen(!isPriceOpen);
        $[151] = isPriceOpen;
        $[152] = setIsPriceOpen;
        $[153] = t57;
      } else {
        t57 = $[153];
      }
      const t58 = `GiftCatalogCore__filterButton justify-between -mx-comfy-xs mt-comfy-xs mb-comfy-xs ${isPriceOpen ? "" : "is-collapsed -mb-comfy-xs"}`;
      let t59;
      if ($[154] !== t) {
        t59 = t("gift-catalog:priceRange");
        $[154] = t;
        $[155] = t59;
      } else {
        t59 = $[155];
      }
      let t60;
      if ($[156] !== Comp3 || $[157] !== t59) {
        t60 = <Text variant={Variants.Label} size={Sizes.Large} prominence={Prominence.Potent} asChild={true}><Comp3>{t59}</Comp3></Text>;
        $[156] = Comp3;
        $[157] = t59;
        $[158] = t60;
      } else {
        t60 = $[158];
      }
      let t61;
      if ($[159] !== t57 || $[160] !== t58 || $[161] !== t60) {
        t61 = <button type="button" onClick={t57} className={t58}>{t60}</button>;
        $[159] = t57;
        $[160] = t58;
        $[161] = t60;
        $[162] = t61;
      } else {
        t61 = $[162];
      }
      let t62;
      if ($[163] !== t56 || $[164] !== t61) {
        t62 = <ButtonStyle variant="secondary" iconAfter={t56}>{t61}</ButtonStyle>;
        $[163] = t56;
        $[164] = t61;
        $[165] = t62;
      } else {
        t62 = $[165];
      }
      let t63;
      if ($[166] !== handlePriceChange || $[167] !== isPriceOpen || $[168] !== priceRange) {
        t63 = isPriceOpen && <div className="GiftCatalogCore__filterBody pb-comfy-sm"><Form onSubmit={_temp2}><SliderInput name="priceRange" min={generalConfig.catalogFilterMin} max={generalConfig.catalogFilterMax} step={10} prefix="$" showInputForm={true} value={priceRange} onCommit={handlePriceChange} /></Form></div>;
        $[166] = handlePriceChange;
        $[167] = isPriceOpen;
        $[168] = priceRange;
        $[169] = t63;
      } else {
        t63 = $[169];
      }
      let t64;
      if ($[170] !== t62 || $[171] !== t63) {
        t64 = <div className="GiftCatalogCore__filterSection grid">{t62}{t63}</div>;
        $[170] = t62;
        $[171] = t63;
        $[172] = t64;
      } else {
        t64 = $[172];
      }
      if ($[173] !== t39 || $[174] !== t45 || $[175] !== t54 || $[176] !== t64) {
        t6 = <aside className={t39}>{t45}{t54}{t55}{t64}</aside>;
        $[173] = t39;
        $[174] = t45;
        $[175] = t54;
        $[176] = t64;
        $[177] = t6;
      } else {
        t6 = $[177];
      }
      const t65 = !isMobileFiltersOpen;
      if ($[178] !== t65) {
        t3 = cn("GiftCatalogCore__main grid gap-comfy-lg", {
          "pt-comfy-xs": t65
        });
        $[178] = t65;
        $[179] = t3;
      } else {
        t3 = $[179];
      }
      t4 = (selectedCategoryFilters.length > 0 || isPriceFiltered) && <div className="flex flex-wrap gap-snug-xl">{selectedCategoryFilters.length > 0 && <ChipGroup chips={selectedCategoryFilters.map(key => ({
          name: categoryLabels[key] || key,
          value: key,
          isSelected: false
        }))} selectedValues={selectedCategoryFilters} handleSelectionChange={handleActiveFilterChange} removable={true} onRemoveChip={value => handleActiveFilterChange(selectedCategoryFilters.filter(v => v !== value))} className="gap-snug-xl" chipSize="sm" />}{isPriceFiltered && <ChipGroup chips={[{
          name: t("gift-catalog:priceRangeChip", {
            min: giftCatalogFilters.priceMin ?? generalConfig.catalogFilterMin,
            max: giftCatalogFilters.priceMax ?? generalConfig.catalogFilterMax
          }),
          value: "price-range",
          isSelected: false
        }]} selectedValues={[]} handleSelectionChange={resetPriceFilter} removable={true} onRemoveChip={resetPriceFilter} className="gap-snug-xl" chipSize="sm" />}</div>;
    }
    $[1] = categories;
    $[2] = error;
    $[3] = giftCatalogFilters;
    $[4] = handleActiveFilterChange;
    $[5] = handlePriceChange;
    $[6] = handleSortChange;
    $[7] = isCategoryOpen;
    $[8] = isMobileFiltersOpen;
    $[9] = isPriceFiltered;
    $[10] = isPriceOpen;
    $[11] = priceRange;
    $[12] = resetAllFilters;
    $[13] = resetPriceFilter;
    $[14] = selectedCategoryFilters;
    $[15] = seoLevel;
    $[16] = setIsCategoryOpen;
    $[17] = setIsMobileFiltersOpen;
    $[18] = setIsPriceOpen;
    $[19] = t;
    $[20] = Comp2;
    $[21] = t10;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    Comp2 = $[20];
    t10 = $[21];
    t3 = $[22];
    t4 = $[23];
    t5 = $[24];
    t6 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[180] !== Comp2 || $[181] !== allResults || $[182] !== currentPage || $[183] !== isLoading || $[184] !== searchResults?.totalCount || $[185] !== seoLevel || $[186] !== t) {
    t11 = isLoading && currentPage === 1 ? <GiftCatalogCoreCardsSkeleton itemCount={generalConfig.catalogPerPage} /> : <><Text variant={Variants.Label} size={Sizes.XLarge} asChild={true}><Comp2>{t("gift-catalog:showingGifts", {
            count: allResults.length,
            countAllGifts: searchResults?.totalCount
          })}</Comp2></Text><div className="grid gap-comfy-sm @md:gap-comfy-lg grid-cols-1 @sm:grid-cols-2 @md:grid-cols-3">{allResults && <TrackViewFundItemList listId="gift_catalog" listName="Gift Catalog" items={allResults.map(_temp3)} isCatalog={true} />}{allResults.map((gift, index) => <GiftCatalogCard seoLevel={seoLevel + 2} key={gift.path || `gift-${index}`} {...gift} variant="grid" onClick={() => trackSelectFundItem("giftCatalog", "Gift Catalog", gift.fund as FundProps, true)} />)}</div></>;
    $[180] = Comp2;
    $[181] = allResults;
    $[182] = currentPage;
    $[183] = isLoading;
    $[184] = searchResults?.totalCount;
    $[185] = seoLevel;
    $[186] = t;
    $[187] = t11;
  } else {
    t11 = $[187];
  }
  let t12;
  if ($[188] !== currentPage || $[189] !== handleLoadMore || $[190] !== hasMore || $[191] !== isLoading || $[192] !== t) {
    t12 = Boolean(hasMore) && <div className="flex justify-center mt-comfy-lg"><ButtonStyle variant="secondary" size="lg"><button type="button" onClick={handleLoadMore} disabled={isLoading}>{isLoading && currentPage > 1 ? t("gift-catalog:loading") : t("gift-catalog:loadMore")}</button></ButtonStyle></div>;
    $[188] = currentPage;
    $[189] = handleLoadMore;
    $[190] = hasMore;
    $[191] = isLoading;
    $[192] = t;
    $[193] = t12;
  } else {
    t12 = $[193];
  }
  let t13;
  if ($[194] !== t11 || $[195] !== t12 || $[196] !== t3 || $[197] !== t4) {
    t13 = <div className={t3}>{t4}{t11}{t12}</div>;
    $[194] = t11;
    $[195] = t12;
    $[196] = t3;
    $[197] = t4;
    $[198] = t13;
  } else {
    t13 = $[198];
  }
  let t14;
  if ($[199] !== t13 || $[200] !== t5 || $[201] !== t6) {
    t14 = <div className={t5}>{t6}{t13}</div>;
    $[199] = t13;
    $[200] = t5;
    $[201] = t6;
    $[202] = t14;
  } else {
    t14 = $[202];
  }
  let t15;
  if ($[203] !== t14 || $[204] !== t7 || $[205] !== t8) {
    t15 = <div className={t7}>{t8}{t14}</div>;
    $[203] = t14;
    $[204] = t7;
    $[205] = t8;
    $[206] = t15;
  } else {
    t15 = $[206];
  }
  let t16;
  if ($[207] !== t15 || $[208] !== t9) {
    t16 = <section className={t9}>{t15}</section>;
    $[207] = t15;
    $[208] = t9;
    $[209] = t16;
  } else {
    t16 = $[209];
  }
  return t16;
};
export const GiftCatalogCore: React.FC<BlockProps> = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <GiftCatalogCoreSkeleton itemCount={generalConfig.catalogPerPage} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><QueryClientProvider client={queryClient}><InternalGiftCatalogCore {...props} /></QueryClientProvider></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
function _temp(a, b) {
  return a.name.localeCompare(b.name);
}
function _temp2() {}
function _temp3(result) {
  return result.fund as FundProps;
}